import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Settings2, Download } from 'lucide-react'
import { useProjectStore } from '@/store/projectStore'
import { useJourneyStore, refreshJourney } from '@/store/journeyStore'
import { useDockBridgeStore } from '@/store/dockBridgeStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { useJourneyAction } from '@/lib/journeyActions'
import { approvePlan, getPlan, getPlanHistory, type PlanDocument } from '@/api/journey'

/**
 * Plan — the specification as a business document (plan §5): what it's for,
 * who uses it, screens, rules, how it will be built, what the AI assumed, what
 * is not included. Before anything is built it is the approval gate; after,
 * it stays as the record of what the app does. The raw specification editor
 * lives at Advanced › Specification editor.
 */
export default function PlanTab() {
  const project = useProjectStore((s) => s.currentProject?.application.folder_name || '')
  const journey = useJourneyStore((s) => s.journey)
  const bridge = useDockBridgeStore()
  const perms = useProjectPermissions()
  const run = useJourneyAction()
  const [doc, setDoc] = useState<PlanDocument | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showFull, setShowFull] = useState(false)
  const [approving, setApproving] = useState(false)
  const [history, setHistory] = useState<Array<{ hash: string; short_hash: string; author: string; date: string; subject: string }> | null>(null)

  const load = useCallback(async () => {
    if (!project) return
    try {
      setDoc(await getPlan(project))
      setError(null)
    } catch (err) {
      console.error('[plan] could not load', err)
      setError('The plan could not be loaded.')
    }
  }, [project])

  useEffect(() => { void load() }, [load])
  // The AI rewrites the specification as it works; follow it.
  useEffect(() => {
    const onChanged = (e: Event) => {
      const p = (e as CustomEvent).detail?.project
      if (!p || p === project) void load()
    }
    window.addEventListener('twasta:agent-files-changed', onChanged as EventListener)
    return () => window.removeEventListener('twasta:agent-files-changed', onChanged as EventListener)
  }, [project, load])

  const built = (journey?.counts.built ?? 0) > 0
  const awaiting = !built && (journey?.agent.awaiting_review || bridge.canContinue)
  const writing = !built && !awaiting && (journey?.agent.asking || journey?.agent.status === 'running')
  const canApprove = !perms.loaded || perms.isOwner || perms.canUpdateSpecs

  const approve = async (stamp: boolean) => {
    if (!project) return
    setApproving(true)
    try {
      if (stamp) await approvePlan(project)
      await run('agent:continue')
      toast.success(stamp ? 'Plan approved — building now' : 'Building now')
      refreshJourney()
      void load()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' && detail ? detail : 'Could not approve the plan')
    } finally {
      setApproving(false)
    }
  }

  const download = () => {
    if (!doc?.markdown) return
    const blob = new Blob([doc.markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${project || 'plan'}-plan.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  const openHistory = async () => {
    try {
      setHistory((await getPlanHistory(project)).commits || [])
    } catch (err) {
      console.error('[plan] history failed', err)
      setHistory([])
    }
  }

  return (
    <div className="swb swb-page">
      <div className="swb-head">
        <div>
          <h1>Plan</h1>
          <p>What your app will do, in plain words. This is the specification the AI builds from.</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {doc?.approved_at && (
            <span className="swb-chip swb-chip-checked">
              Approved {new Date(doc.approved_at * 1000).toLocaleDateString()}{doc.approved_by ? ` by ${doc.approved_by}` : ''}
            </span>
          )}
          {doc?.changed_since_approval && <span className="swb-chip swb-chip-to_check">Changed since approval</span>}
          <button type="button" className="swb-btn swb-btn-secondary" onClick={() => void run('tab:settings')}>
            <Settings2 size={15} /> App settings
          </button>
          <button type="button" className="swb-btn swb-btn-secondary" onClick={download} disabled={!doc?.has_spec}
            title="Download the specification (Markdown — opens in Word)">
            <Download size={15} /> Download
          </button>
        </div>
      </div>

      {error && (
        <div className="swb-notice swb-notice-warn">
          <span style={{ flex: 1 }}>{error}</span>
          <button type="button" className="swb-btn swb-btn-secondary" onClick={() => void load()}>Try again</button>
        </div>
      )}
      {!doc && !error && <div className="spinner" style={{ margin: '2rem auto' }} />}

      {doc && !doc.has_spec && (journey?.counts.planned ?? 0) === 0 && (
        <section className="swb-card swb-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
          <h2 className="swb-section-title" style={{ fontSize: '1.125rem' }}>No plan yet</h2>
          <p className="swb-muted" style={{ margin: 0, maxWidth: 560 }}>
            Describe your app first. The AI asks a few questions, then writes the plan here for you to read
            before anything is built.
          </p>
          <button type="button" className="swb-btn swb-btn-primary" onClick={() => void run('tab:next')}>Describe my app</button>
        </section>
      )}

      {doc && (doc.has_spec || (journey?.counts.planned ?? 0) > 0) && (
        <>
          {writing && (
            <div className="swb-notice swb-notice-warn">
              <span style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column' }}>
                <strong>The plan is still being written</strong>
                <span style={{ fontSize: 14 }}>{journey?.agent.asking ? 'The AI has a question for you.' : 'The parts below may still change.'}</span>
              </span>
              {journey?.agent.asking && (
                <button type="button" className="swb-btn swb-btn-primary" onClick={() => void run('agent:show')}>Answer it</button>
              )}
            </div>
          )}
          {awaiting && (
            <div className="swb-notice swb-notice-info">
              <span style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column' }}>
                <strong style={{ fontSize: '1rem' }}>Nothing is built yet</strong>
                <span style={{ fontSize: 14 }}>
                  Read the plan and change anything in plain words. You can also{' '}
                  <button type="button" className="swb-link" onClick={() => void run('tab:settings')}>review the app settings</button>
                  {' '}(database, business logic). The AI starts only when you approve it.
                </span>
              </span>
              <button type="button" className="swb-btn swb-btn-secondary swb-btn-lg" onClick={() => void run('ask:Change the plan: ')}>
                Ask for a change
              </button>
              {canApprove && (
                <button type="button" className="swb-btn swb-btn-primary swb-btn-lg" disabled={approving} onClick={() => void approve(true)}
                  data-tw-action="approve-plan">
                  {approving ? 'Starting…' : 'Approve and build'}
                </button>
              )}
            </div>
          )}
          {built && (
            <div className="swb-notice swb-notice-plain">
              <span style={{ flex: '1 1 320px', fontSize: 14 }}>
                Your app was built from this plan. When you ask the AI for a change, the plan is updated too,
                so it always describes the app as it is.
              </span>
              <button type="button" className="swb-link" onClick={() => void openHistory()}>See earlier versions</button>
            </div>
          )}

          {history && (
            <section className="swb-card" aria-label="Earlier versions">
              <div className="swb-band"><h2>Earlier versions</h2>
                <button type="button" className="swb-link" onClick={() => setHistory(null)}>Close</button>
              </div>
              {history.length === 0 ? (
                <p className="swb-muted" style={{ margin: 0, padding: 16 }}>
                  No saved versions yet. They appear once the app is committed to source control.
                </p>
              ) : (
                <ul className="swb-list">
                  {history.map((c) => (
                    <li key={c.hash}>
                      <span style={{ flex: '1 1 260px' }}><strong style={{ fontSize: 14 }}>{c.subject}</strong>
                        <span className="swb-muted" style={{ display: 'block' }}>{c.author} · {new Date(c.date).toLocaleString()}</span></span>
                      <code style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{c.short_hash}</code>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
            <nav aria-label="Plan sections" className="swb-card" style={{ flex: '1 1 200px', maxWidth: 240, padding: '10px 0', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0 }}>
              {doc.sections.map((sec) => (
                <a key={sec.id} href={`#plan-${sec.id}`}
                  onClick={(e) => { e.preventDefault(); document.getElementById(`plan-${sec.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 16px', fontSize: 14, fontWeight: 500, color: 'var(--color-text)', textDecoration: 'none' }}>
                  <span>{sec.title}</span>
                  {sec.count > 0 && <span className="swb-muted">{sec.count}</span>}
                </a>
              ))}
            </nav>
            <article className="swb-card" style={{ flex: '999 1 480px', minWidth: 0, padding: '8px 28px 24px' }}>
              {doc.sections.map((sec) => (
                <section key={sec.id} id={`plan-${sec.id}`} style={{ padding: '18px 0', borderBottom: '1px solid var(--color-surface-2)', display: 'flex', flexDirection: 'column', gap: 10, scrollMarginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>{sec.title}</h2>
                    <button type="button" className="swb-btn swb-btn-quiet swb-btn-sm" aria-label={`Change ${sec.title}`}
                      onClick={() => void run(sec.change)}>
                      {sec.change.startsWith('open:settings') ? 'Review in app settings' : 'Change'}
                    </button>
                  </div>
                  {sec.text && <p style={{ margin: 0, fontSize: '0.9375rem', maxWidth: 680 }}>{sec.text}</p>}
                  {sec.rows.length > 0 ? (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {sec.rows.map((r, i) => (
                        <li key={i} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 12px', padding: r.heading ? '10px 0 2px' : '6px 0' }}>
                          {r.title && (
                            <span style={{ flex: r.line ? '0 0 200px' : '1 1 auto', fontWeight: 600, fontSize: r.heading ? '0.9375rem' : '0.9375rem', color: r.heading ? 'var(--color-accent-hvr)' : undefined }}>
                              {r.title}
                            </span>
                          )}
                          {r.line && <span style={{ flex: '1 1 280px', fontSize: 14 }}>{r.line}</span>}
                          {r.tag && <span className={`swb-chip ${r.tag === 'Assumed' ? 'swb-chip-to_check' : 'swb-chip-neutral'}`}>{r.tag}</span>}
                        </li>
                      ))}
                    </ul>
                  ) : !sec.text ? (
                    <p className="swb-muted" style={{ margin: 0 }}>Nothing written here yet.</p>
                  ) : null}
                </section>
              ))}
              {doc.has_spec && (
                <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <button type="button" className="swb-link" style={{ alignSelf: 'flex-start' }} onClick={() => setShowFull((v) => !v)}>
                    {showFull ? 'Hide the full text' : 'Read the full text'}
                  </button>
                  {showFull && (
                    <div className="markdown-content" style={{ fontSize: '0.9375rem' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.markdown}</ReactMarkdown>
                    </div>
                  )}
                  <p className="swb-muted" style={{ margin: 0 }}>
                    Need the detailed technical specification? Open it from{' '}
                    <button type="button" className="swb-link" onClick={() => void run('tab:advanced/specification')}>
                      Advanced › Specification editor
                    </button>.
                  </p>
                </div>
              )}
            </article>
          </div>
        </>
      )}
    </div>
  )
}
